import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login, org, loadOrg, user } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/setup-status").then(({ data }) => { if (data.needs_setup) navigate("/kurulum"); });
    if (!org) loadOrg();
  }, []); // eslint-disable-line

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      login(data.token, data.user);
      navigate("/");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white">
        <div className="flex items-center gap-3">
          {org?.logo ? <img src={org.logo} alt="logo" className="h-11 w-11 rounded-lg object-cover bg-white" /> :
            <div className="h-11 w-11 rounded-lg bg-sky-600 flex items-center justify-center"><ShieldCheck className="h-6 w-6" /></div>}
          <div>
            <div className="font-display font-bold text-lg">{org?.short_name || "TMGD Portalı"}</div>
            <div className="text-xs text-slate-400">Tehlikeli Madde Güvenlik Danışmanlığı</div>
          </div>
        </div>
        <div className="space-y-4">
          <h1 className="font-display text-4xl font-bold leading-tight">Kuruluş, firma ve belge yönetimi tek portalda</h1>
          <p className="text-slate-300 max-w-md">Danışman, müşteri ve kuruluş yönetimi role göre açılır. Gerçek yetkilendirme, faaliyet bazlı klasör yapısı ve güvenli erişim.</p>
        </div>
        <div className="text-xs text-slate-500">{org?.full_title}</div>
      </div>
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardContent className="pt-8 pb-6">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-sky-600 flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-white" /></div>
              <div className="font-display font-bold">{org?.short_name || "TMGD Portalı"}</div>
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Giriş Yap</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">Hesabınızla portala erişin. Kayıt olma yoktur; hesaplar yönetici tarafından açılır.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>E-posta</Label>
                <Input data-testid="login-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Parola</Label>
                <Input data-testid="login-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
              <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={loading} data-testid="login-submit-button">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Giriş Yap
              </Button>
              <div className="text-center">
                <Link to="/sifremi-unuttum" className="text-sm text-sky-600 hover:underline" data-testid="forgot-password-link">Parolamı unuttum</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
